import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  InputAdornment,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import { Done as ApplyIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { layoutGap } from '../../../theme/theme';
import { getWorkspaceActionButtonSx } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';

interface ValidationMarginControlProps {
  value: number;
  defaultValue: number;
  min: number;
  max: number;
  disabled?: boolean;
  saving?: boolean;
  onCommit: (value: number) => Promise<void> | void;
}

function formatMargin(value: number): string {
  return Number.isFinite(value) ? value.toFixed(1) : '';
}

export default function ValidationMarginControl({
  value,
  defaultValue,
  min,
  max,
  disabled = false,
  saving = false,
  onCommit,
}: ValidationMarginControlProps) {
  const theme = useTheme();
  const [draft, setDraft] = useState(formatMargin(value));

  useEffect(() => {
    setDraft(formatMargin(value));
  }, [value]);

  const parsedDraft = Number(draft);
  const hasChanged = Number.isFinite(parsedDraft) && Math.abs(parsedDraft - value) > 0.05;
  const isValid = Number.isFinite(parsedDraft) && parsedDraft >= min && parsedDraft <= max;
  const helperText = useMemo(
    () => `${formatMargin(min)}-${formatMargin(max)} mm, default ${formatMargin(defaultValue)}`,
    [defaultValue, max, min]
  );

  const handleCommit = async () => {
    if (!isValid || disabled || saving) {
      return;
    }

    await onCommit(parsedDraft);
  };

  return (
    <Stack
      direction="row"
      spacing={layoutGap.tight}
      alignItems="center"
      useFlexGap
      sx={{
        minWidth: 0,
        flex: { xs: '1 1 100%', sm: '0 1 auto' },
      }}
    >
      <TextField
        type="number"
        size="small"
        label="Gap allowance"
        value={draft}
        disabled={disabled || saving}
        error={draft.trim().length > 0 && !isValid}
        helperText={helperText}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void handleCommit();
          }
        }}
        InputProps={{
          endAdornment: <InputAdornment position="end">mm</InputAdornment>,
          inputProps: {
            min,
            max,
            step: 0.5,
            'aria-label': 'Gap allowance',
          },
        }}
        sx={{
          width: { xs: '100%', sm: 180 },
          minWidth: { xs: 0, sm: 160 },
          '& .MuiInputBase-root': {
            minHeight: 30,
            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.88 : 0.28),
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.76rem',
          },
          '& .MuiInputBase-input': {
            py: 0.45,
            fontSize: '0.78rem',
            fontWeight: 700,
          },
          '& .MuiFormHelperText-root': {
            mx: 0,
            mt: 0.25,
            fontSize: '0.64rem',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          },
        }}
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={<ApplyIcon />}
        disabled={disabled || saving || !hasChanged || !isValid}
        onClick={() => void handleCommit()}
        sx={[
          getWorkspaceActionButtonSx(theme, { tone: 'secondary', minHeight: 30 }),
          {
            px: 1,
            flex: '0 0 auto',
          },
        ]}
      >
        {saving ? 'Saving' : 'Apply'}
      </Button>
    </Stack>
  );
}
